import { applyCaptionTemplateOnEditor } from '@src/editor/services/applyCaptionsLanguage';
import { produceFirstCaptions } from '@src/hooks/produceFirstCaptions';
import { notifyCaptionFontChange } from '@src/hooks/switchCaptionLanguage';
import { setCaptionsHidden } from '@src/services/karaoke/captionsVisibility';
import {
  useEditor,
  useKaraokeStore,
  useTranscription,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { analytics } from '@src/utility/analytics';
import { resolveCaptionLanguageKey } from '@src/utility/captionLanguage';

import type { CaptionFallbackTemplate } from '@src/editor/services/applyCaptionsLanguage';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

export interface CaptionsToggle {
  /** The video has a caption track (produced, not cleared). */
  hasTrack: boolean;
  /** Captions are drawn on the video. */
  visible: boolean;
  /** The template the video's caption style came from; `null` when none. */
  templateId: string | null;
  /**
   * Turn the video's captions on or off. On with no track produces one
   * (`produceFirstCaptions`); off keeps the lines and draws them invisible, so
   * on again is the same lines back with no re-produce.
   */
  setVisible: (visible: boolean) => void;
  /**
   * PLAT-11877 — put this template's style on the video's captions and
   * re-produce them (one undo entry), shown. The Captions picker's template
   * rows.
   */
  applyTemplate: (template: CaptionFallbackTemplate) => void;
}

/**
 * PLAT-11689 — the video's captions, on and off, the same thing on every
 * surface that offers it (clip preview, Creator Studio). It never changes
 * which view the transcript shows — that is `TranscriptModeToggle`'s job.
 * Reads the karaoke store of whichever store scope it is called in (the clip
 * preview's clip-scoped stores included), so call it inside that provider.
 *
 * PLAT-11877 — was the transcript header's Cap pill; now the Captions picker
 * beside the player (`PlayerAudioPickers`), as Dubbing Review has it.
 */
/** What `useCaptionsToggle` reads from its host (PLAT-12101). */
export type CaptionsToggleHost = Pick<
  ITranscriptVideoCreator,
  'story' | 'currentVideo' | 'showToast'
>;

export function useCaptionsToggle(
  videoCreator: CaptionsToggleHost,
  captionFallbackTemplate?: CaptionFallbackTemplate,
): CaptionsToggle {
  const karaokeStore = useKaraokeStore();
  const editorStore = useEditor();
  const videoSourceStore = useVideoSource();
  const transcriptionStore = useTranscription();
  const hasTrack = karaokeStore.hasKaraokeLines;
  const hidden = karaokeStore.captionsHidden;

  const setVisible = (visible: boolean) => {
    const editor = editorStore.editor;
    if (!editor) return;
    if (visible === (hasTrack && !hidden)) return;
    if (visible && !hasTrack) {
      // No track (never produced, or post-Clear): add one. PLAT-10635 /
      // PLAT-11674: seed the org's template and match the font to the
      // language before the first produce. The produce carries the undo
      // entry (and the clip preview's autosave), and starts shown
      // (`commandProduceKaraoke` clears a leftover hidden flag itself).
      const produced = produceFirstCaptions({
        editor,
        fallbackTemplate: captionFallbackTemplate,
        transcriptLanguage: transcriptionStore.rawLanguage,
        originalVideoLanguage: videoCreator.story?.originalVideoLanguage,
        showToast: toast => videoCreator.showToast(toast),
      });
      // A failed produce (no active tokens) added no captions: nothing to
      // report or repaint.
      if (!produced) return;
    } else {
      setCaptionsHidden(editor, !visible);
    }
    analytics.track('transcript_toggle_karaoke', {
      storyId: videoCreator.story?.id,
      storyTitle: videoCreator.story?.title,
      videoId: videoCreator.currentVideo?.id,
      videoTitle: videoCreator.currentVideo?.title,
      captions_visible: visible,
    });
    // CLAUDE.md: a commandX from a UI surface pairs with an explicit repaint
    // (`editor` is not MobX-tracked).
    karaokeStore.syncFromEditor(editor);
    videoSourceStore?.refreshSource();
  };

  const applyTemplate = (template: CaptionFallbackTemplate) => {
    const editor = editorStore.editor;
    if (!editor) return;
    if (hidden)
      setCaptionsHidden(editor, false, { createUndoRedoEntry: false });
    const substitution = applyCaptionTemplateOnEditor(editor, template, {
      targetLanguageKey: resolveCaptionLanguageKey(
        editor.getDefaultKaraokeConfig()?.language,
        transcriptionStore.rawLanguage,
        videoCreator.story?.originalVideoLanguage,
      ),
    });
    // The config update above carried the undo entry.
    if (
      !editor.commandProduceKaraoke({
        replaceTrack: true,
        createUndoRedoEntry: false,
      })
    )
      return;
    notifyCaptionFontChange(
      toast => videoCreator.showToast(toast),
      substitution,
    );
    analytics.track('transcript_caption_template_picked', {
      storyId: videoCreator.story?.id,
      videoId: videoCreator.currentVideo?.id,
      template_id: template.templateId,
    });
    karaokeStore.syncFromEditor(editor);
    videoSourceStore?.refreshSource();
  };

  return {
    hasTrack,
    visible: hasTrack && !hidden,
    templateId: karaokeStore.defaultKaraokeConfig?.parent_template_id ?? null,
    setVisible,
    applyTemplate,
  };
}
